'use client';

import { useState } from 'react';
import { momentTimezone } from 'src/utils/time';

import ChipFilterItem from '@/app/(football)/[team]/_components/common/ChipFilterItem';
import { useTeamConfig } from '@/app/(football)/[team]/_context/context';
import type { TopCollectorSeason } from '@/store/server/common/football/football.dto';
import { useGetTopCollectorPeriodQuery } from '@/store/server/common/football/football.query';
import { useGetOriginalsInitQuery } from '@/store/server/common/originals/originals.query';

import BenefitSection from './Section/BenefitSection';
import HeadingSection from './Section/HeadingSection';
import RankingSection from './Section/RankingSection';
import TimerSection from './Section/TimerSection';

export default function TopCollectorContainer() {
  const { teamSlug, topCollector, packName, originalsName } = useTeamConfig();

  // 사용자가 명시적으로 선택한 시즌 (없으면 config 기반 기본값을 따름)
  const [selectedSeason, setSelectedSeason] = useState<TopCollectorSeason | null>(null);

  // config가 하반기여도, 실제 하반기 시작 시각 전(또는 기간 조회 실패)이면 상반기만 노출
  const isSecondConfigured = topCollector.currentSeason === 'second_top_collector';

  const { data: initData } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });
  const packIdx = initData?.[originalsName]?.packList.find((p) => p.packName === packName)?.packIdx ?? 0;

  const { data: secondPeriod, isLoading: isSecondPeriodLoading } = useGetTopCollectorPeriodQuery({
    // 하반기 config일 때만 조회 (packIdx 0 → 쿼리 비활성)
    packIdx: isSecondConfigured ? packIdx : 0,
    season: 'second_top_collector',
  });

  const isSecondSeasonStarted =
    isSecondConfigured &&
    !!secondPeriod?.startDateTime &&
    momentTimezone().isSameOrAfter(momentTimezone(secondPeriod.startDateTime));

  // 하반기 config인데 기간 조회가 끝났고 아직 시작 전(또는 조회 실패)으로 확정된 상태
  const isSecondSeasonBlocked =
    isSecondConfigured && packIdx !== 0 && !isSecondPeriodLoading && !isSecondSeasonStarted;

  // config가 first면 무조건 비활, second면 차단 확정 시에만 비활
  const isSecondSeasonDisabled = !isSecondConfigured || isSecondSeasonBlocked;

  // 기본 시즌: config를 따르되 하반기 차단 확정 시 상반기. 사용자가 고른 값이 있으면 우선.
  const defaultSeason = isSecondSeasonBlocked ? 'first_top_collector' : topCollector.currentSeason;
  const season = selectedSeason ?? defaultSeason;

  return (
    <div
      className="flex flex-col items-center px-5 pt-[50px] pb-football-page-bottom"
      style={{
        backgroundColor: 'var(--color-team-primary-50)',
        backgroundImage: `url('https://cdn.kottonseed.io/assets/football/2026/${teamSlug}/top-collector/bg.webp')`,
        backgroundSize: '480px auto',
        backgroundPosition: 'top center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <HeadingSection />

      {/* Season Filter */}
      <div role="listbox" className="flex items-center gap-2 pb-8">
        <ChipFilterItem
          label="상반기"
          isSelected={season === 'first_top_collector'}
          onClick={() => setSelectedSeason('first_top_collector')}
        />
        <ChipFilterItem
          label="하반기"
          isSelected={season === 'second_top_collector'}
          disabled={isSecondSeasonDisabled}
          onClick={() => setSelectedSeason('second_top_collector')}
        />
      </div>

      <TimerSection season={season} />
      <BenefitSection season={season} />
      <RankingSection season={season} />
    </div>
  );
}
